// File: src/components/layout/AppHeader.tsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import ThemeToggleButton from "../components/common/ThemeToggleButton";
import NotificationDropdown from "../components/header/NotificationDropdown";
import UserDropdown from "../components/header/UserDropdown";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { headerStyles } from "../styles/layout/AppHeader.styles";
import { useSettings } from "../context/SettingsContext";
import { CrossHamburgerIcon, HamburgerIcon, MoreOptionsIcon } from "../components/svg/Icons";
import { useLogoSrc } from "../hooks/useLogoSrc";

const AppHeader: React.FC = () => {
  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false);
  const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();
  const { settings, fetchSettings } = useSettings();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const token = sessionStorage.getItem("access_token");
  const { logoSrc, displayName } = useLogoSrc();
  // Fetch settings on mount to ensure latest colors are loaded
  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [fetchSettings, token]);

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const handleToggle = () => {
    if (window.innerWidth >= 991) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  const toggleApplicationMenu = () => {
    setApplicationMenuOpen(!isApplicationMenuOpen);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <header
      className={headerStyles.header}
      style={{
        backgroundColor: isDarkMode ? '#1f2937' : settings.headerColor || '#ffffff',
        color: isDarkMode ? undefined : settings.fontColor || '#000000',
      }}
    >
      <ToastContainer position={headerStyles.toastContainer as any} autoClose={1000} />
      <div className={headerStyles.container}>
        <div className={headerStyles.innerContainer}>
          <button
            className={headerStyles.toggleButton}
            onClick={handleToggle}
            aria-label="Toggle Sidebar" 
            style={{
              color: isDarkMode ? '#d1d5db' : settings.fontColor || '#000000',
            }}
          >
            {isMobileOpen ? (
              <CrossHamburgerIcon/>
            ) : (
              <HamburgerIcon/>
            )}
          </button>

          <div className={headerStyles.logoContainer}>
            <Link to="/">
              <img
                className={headerStyles.logo}
                src={logoSrc}
                alt={displayName}
              />
              <img
                className={headerStyles.logoDark}
                src={logoSrc}
                alt={displayName}
              />
            </Link>
            <span
              className={headerStyles.title}
              style={{
                color: isDarkMode ? undefined : settings.fontColor || '#000000',
              }}
            >
              {displayName}
            </span>
          </div>

          <button
            onClick={toggleApplicationMenu}
            className={headerStyles.appMenuButton}
            style={{
              backgroundColor: isApplicationMenuOpen && !isDarkMode
                ? settings.menuActiveColor || '#f8f7f7'
                : undefined,
              color: isDarkMode ? '#d1d5db' : settings.fontColor || '#000000',
            }}
          >
            <MoreOptionsIcon/>
          </button>
        </div>
        <div
          className={`${isApplicationMenuOpen ? "flex" : "hidden"} ${headerStyles.menuContainer}`}
          style={{
            backgroundColor: isDarkMode ? undefined : settings.headerColor || '#ffffff',
            color: isDarkMode ? undefined : settings.fontColor || '#000000',
          }}
        >
          <div className={headerStyles.menuInner}>
            <ThemeToggleButton />
            <NotificationDropdown />
          </div>
          <UserDropdown />
        </div>
      </div>
    </header>
  );
};

export default AppHeader;